import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPost } from "../../api";

const detailOf = (err) =>
  (err && err.response && err.response.data && err.response.data.detail) || "";

// At ~1,000 clients, rendering every unmatched row means a <select> of every
// unmatched customer per row -- on the order of a million DOM nodes. Capping
// what is RENDERED is what makes the screen usable; it never changes what is
// selected or saved.
const ROW_CAP = 25;
// Options are the other half of the problem: every unmatched row carries a
// <select> of every unmatched customer, so the node count is rows x options.
// Searching narrows both.
const OPTION_CAP = 100;

const matches = (needle, ...haystack) => {
  if (!needle) return true;
  const n = needle.trim().toLowerCase();
  return haystack.some((h) => (h || "").toLowerCase().includes(n));
};

export default function CustomerMappingTab() {
  const [data, setData] = useState(null);
  const [selection, setSelection] = useState({});   // client_id -> qbo_customer_id
  // qbo_customer_id -> "true" | "false" | undefined. No default: forcing a
  // deliberate choice is the point -- an unreviewed default is what
  // silently posted QuickBooks-imported clients as personal before.
  const [corpChoice, setCorpChoice] = useState({});
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  // client_ids the user has explicitly chosen for (checked/unchecked/picked from
  // the select) — a reload must not clobber these, even a deliberate "off".
  const touchedRef = useRef(new Set());

  const load = () =>
    apiGet("/api/quickbooks/customers/mapping/")
      .then((d) => {
        setData(d);
        const autoMap = {};
        (d.auto || []).forEach((m) => { autoMap[m.client_id] = m.qbo_customer_id; });
        const validIds = new Set([
          ...(d.auto || []).map((m) => m.client_id),
          ...(d.near_miss || []).map((m) => m.client_id),
          ...(d.unmatched_clients || []).map((m) => m.client_id),
        ]);
        // Drop touched-tracking for clients that no longer appear at all
        // (e.g. they just got linked and left the payload).
        Array.from(touchedRef.current).forEach((id) => {
          if (!validIds.has(id)) touchedRef.current.delete(id);
        });
        setSelection((prev) => {
          const next = {};
          validIds.forEach((id) => {
            if (touchedRef.current.has(id)) {
              // The user already decided this one — including "off" — keep it.
              next[id] = prev[id] || "";
            } else {
              // Untouched: seed from the auto bucket as before; everything
              // else (near-miss, unmatched) starts unselected.
              next[id] = autoMap[id] || "";
            }
          });
          return next;
        });
      })
      .catch((err) => setError(
        detailOf(err) || "Could not reach QuickBooks. Check the connection under Settings."));

  useEffect(() => { load(); }, []);

  if (error) return <div>{error}</div>;
  if (!data) return <div>Loading…</div>;

  const confirm = async () => {
    const byId = {};
    (data.auto || []).concat(data.near_miss || []).forEach((r) => {
      byId[r.qbo_customer_id] = r.qbo_display_name;
    });
    (data.unmatched_customers || []).forEach((r) => {
      byId[r.qbo_customer_id] = r.qbo_display_name;
    });
    const links = Object.entries(selection)
      .filter(([, qid]) => qid)
      .map(([client_id, qbo_customer_id]) => ({
        client_id: Number(client_id),
        qbo_customer_id,
        qbo_display_name: byId[qbo_customer_id] || "",
      }));
    const res = await apiPost("/api/quickbooks/customers/confirm/", { links });
    setMsg(`Confirmed ${res.confirmed} mapping(s). Nothing was written to QuickBooks.`);
  };

  const createInQbo = async (client) => {
    setMsg(""); setError("");
    try {
      await apiPost("/api/quickbooks/customers/create_in_qbo/", { client_id: client.client_id });
      setMsg(`Created ${client.client_name} in QuickBooks.`);
      load();
    } catch (err) {
      setError(detailOf(err) || "Could not create the customer.");
    }
  };

  const createInClientZone = async (cust) => {
    setMsg(""); setError("");
    const choice = corpChoice[cust.qbo_customer_id];
    if (choice !== "true" && choice !== "false") {
      setError(`Choose Corporate or Personal for "${cust.qbo_display_name}" before creating it.`);
      return;
    }
    try {
      await apiPost("/api/quickbooks/customers/create_in_clientzone/", {
        qbo_customer_id: cust.qbo_customer_id,
        qbo_display_name: cust.qbo_display_name,
        is_corporate: choice === "true",
      });
      setMsg(`Created ${cust.qbo_display_name} in ClientZone.`);
      load();
    } catch (err) {
      setError(detailOf(err) || "Could not create the client.");
    }
  };

  const setChoice = (clientId, qid) => {
    touchedRef.current.add(clientId);
    setSelection((s) => ({ ...s, [clientId]: qid }));
  };

  const toggle = (clientId, qid, checked) => setChoice(clientId, checked ? qid : "");

  const auto = (data.auto || []).filter((m) => matches(query, m.client_name, m.qbo_display_name));
  const nearMiss = (data.near_miss || []).filter((m) => matches(query, m.client_name, m.qbo_display_name));
  const unmatchedClients = (data.unmatched_clients || []).filter((c) => matches(query, c.client_name));
  const unmatchedCustomers = (data.unmatched_customers || []).filter((q) => matches(query, q.qbo_display_name));
  // The ambiguous bucket is never capped and never hidden by an empty search:
  // it is the one that actually has to be dealt with, and there are few of them.
  const ambiguous = (data.ambiguous || []).filter(
    (g) => matches(query, ...g.clients.map((c) => c.client_name)));

  const linkedCount = (data.links || []).length;
  const totalKnown = linkedCount + (data.auto || []).length + (data.near_miss || []).length
    + (data.unmatched_clients || []).length
    + (data.ambiguous || []).reduce((n, g) => n + g.clients.length, 0);

  const capped = (rows) => rows.slice(0, ROW_CAP);
  const moreNote = (shown, total) =>
    total > shown ? (
      <div style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 12px" }}>
        showing {shown} of {total} — search to narrow
      </div>
    ) : null;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label htmlFor="cust-search" style={{ marginRight: 8 }}>Search</label>
        <input
          id="cust-search"
          aria-label="Search clients and QuickBooks customers"
          value={query}
          placeholder="client or QuickBooks name"
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 280 }}
        />
        <div style={{ fontSize: 13, color: "#374151", marginTop: 6 }}>
          {linkedCount} of {totalKnown} clients linked to QuickBooks
        </div>
      </div>

      <h2>Duplicates to resolve ({ambiguous.length})</h2>
      {ambiguous.length === 0 && <p>None. </p>}
      {ambiguous.map((g) => (
        <div key={g.normalized} data-testid={`ambiguous-${g.normalized}`}
             style={{ border: "1px solid #fca5a5", padding: 8, marginBottom: 8 }}>
          <strong>{g.clients.map((c) => c.client_name).join("  ·  ")}</strong>
          <div style={{ fontSize: 13 }}>
            These are one client in QuickBooks' eyes. Merge them before mapping —{" "}
            <a href="/clients">open the Clients tab</a>.
          </div>
        </div>
      ))}

      <h2>Auto-matched ({auto.length})</h2>
      {(data.auto || []).length > ROW_CAP && (
        <div style={{ fontSize: 13, color: "#92400e", marginBottom: 6 }}>
          Confirm will link all {(data.auto || []).length}, not just those shown.
        </div>
      )}
      {capped(auto).map((m) => (
        <div key={m.client_id}>
          <label>
            <input type="checkbox"
              checked={selection[m.client_id] === m.qbo_customer_id}
              onChange={(e) => toggle(m.client_id, m.qbo_customer_id, e.target.checked)} />
            {m.client_name} → {m.qbo_display_name}
          </label>
        </div>
      ))}
      {moreNote(Math.min(ROW_CAP, auto.length), auto.length)}

      <h2>Near misses ({nearMiss.length})</h2>
      {capped(nearMiss).map((m) => (
        <div key={m.client_id}>
          <label htmlFor={`near-${m.client_id}`}>
            <input id={`near-${m.client_id}`}
              aria-label={`${m.client_name} → ${m.qbo_display_name}`}
              type="checkbox"
              checked={selection[m.client_id] === m.qbo_customer_id}
              onChange={(e) => toggle(m.client_id, m.qbo_customer_id, e.target.checked)} />
            {m.client_name} → {m.qbo_display_name} ({m.score})
          </label>
        </div>
      ))}
      {moreNote(Math.min(ROW_CAP, nearMiss.length), nearMiss.length)}

      <h2>Clients with no QuickBooks customer ({unmatchedClients.length})</h2>
      {capped(unmatchedClients).map((c) => (
        <div key={c.client_id}>
          {c.client_name}{" "}
          <select value={selection[c.client_id] || ""}
            onChange={(e) => setChoice(c.client_id, e.target.value)}>
            <option value="">— leave unmapped —</option>
            {unmatchedCustomers.slice(0, OPTION_CAP).map((q) => (
              <option key={q.qbo_customer_id} value={q.qbo_customer_id}>
                {q.qbo_display_name}
              </option>
            ))}
            {unmatchedCustomers.length > OPTION_CAP && (
              <option value="" disabled>
                — {unmatchedCustomers.length - OPTION_CAP} more; search to narrow —
              </option>
            )}
          </select>{" "}
          <button onClick={() => createInQbo(c)}>
            Create “{c.client_name}” in QuickBooks
          </button>
        </div>
      ))}
      {moreNote(Math.min(ROW_CAP, unmatchedClients.length), unmatchedClients.length)}

      <h2>QuickBooks customers with no client ({unmatchedCustomers.length})</h2>
      {capped(unmatchedCustomers).map((q) => (
        <div key={q.qbo_customer_id}>
          {q.qbo_display_name}{" "}
          <select
            aria-label={`${q.qbo_display_name} client type`}
            value={corpChoice[q.qbo_customer_id] || ""}
            onChange={(e) => setCorpChoice((s) => ({
              ...s, [q.qbo_customer_id]: e.target.value,
            }))}>
            <option value="">— choose type —</option>
            <option value="true">Corporate</option>
            <option value="false">Personal</option>
          </select>{" "}
          <button onClick={() => createInClientZone(q)}>
            Create “{q.qbo_display_name}” in ClientZone
          </button>
        </div>
      ))}

      <div style={{ marginTop: 16 }}>
        <button onClick={confirm}>Confirm mappings</button>
        {msg && <span style={{ marginLeft: 12, color: "#065f46" }}>{msg}</span>}
      </div>
    </div>
  );
}
